import { createContext, PropsWithChildren, useContext } from 'react';

import { PieProps } from 'recharts';

import { PieChartData, PieChartVariant } from './types';

type ChartData<T extends PieChartData> = PieProps<T>['data'];

type PieChartContextValue<T extends PieChartData> = {
  data?: ChartData<T>;
  radius: number;
  variant: PieChartVariant;
  startAngle: number;
  endAngle: number;
  isHalfCircle: boolean;
  rootWidth: number;
  rootHeight: number;
  containerWidth?: number;
};

const PieChartContext =
  createContext<PieChartContextValue<PieChartData> | null>(null);

export const usePieChartContext = <T extends PieChartData>() => {
  const ctx = useContext(PieChartContext) as PieChartContextValue<T>;
  if (!ctx) {
    throw new Error('usePieChartContext must be used within PieChartContainer');
  }
  return ctx;
};

type PieChartContextProviderProps<T extends PieChartData> = PropsWithChildren<{
  data?: ChartData<T>;
  radius: number;
  variant: PieChartVariant;
}>;

const anglesByVariant: Record<
  PieChartVariant,
  { startAngle: number; endAngle: number }
> = {
  full: {
    // Start filling from the top
    startAngle: 450,
    endAngle: 90,
  },
  'half-left': {
    startAngle: 90,
    endAngle: 270,
  },
  'half-right': {
    startAngle: 90,
    endAngle: -90,
  },
};

export const PieChartProvider = <T extends PieChartData>({
  data,
  radius,
  variant,
  children,
}: PieChartContextProviderProps<T>) => {
  const isHalfCircle = ['half-left', 'half-right'].includes(variant);

  const startAngle = anglesByVariant[variant].startAngle;
  const endAngle = anglesByVariant[variant].endAngle;

  return (
    <PieChartContext.Provider
      value={{
        data,
        radius,
        startAngle,
        endAngle,
        variant,
        isHalfCircle,
        rootWidth: isHalfCircle ? radius : radius * 2,
        rootHeight: radius * 2,
        containerWidth: isHalfCircle ? radius * 2 : undefined,
      }}
    >
      {children}
    </PieChartContext.Provider>
  );
};
